/**
 * Instance Customization Page - Logo, Colors, Theme
 */
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useInstance } from '../../contexts/InstanceContext';
import DEFAULT_LOGOS from '../../lib/logos';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Separator } from '../../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { HelpAlert, HelpTooltip } from '../../components/ui/HelpGuide';
import { 
  Palette, 
  Save, 
  Image as ImageIcon, 
  RefreshCw,
  Sun,
  Moon,
  Eye,
  Upload,
  Trash2,
  Loader2,
} from 'lucide-react';

// Predefined color palettes
const COLOR_PALETTES = [
  { name: 'Laranja (Padrão)', primary: '#f97316', secondary: '#3b82f6' },
  { name: 'Azul', primary: '#3b82f6', secondary: '#f97316' },
  { name: 'Verde', primary: '#10b981', secondary: '#8b5cf6' },
  { name: 'Roxo', primary: '#8b5cf6', secondary: '#ec4899' },
  { name: 'Rosa', primary: '#ec4899', secondary: '#3b82f6' },
  { name: 'Vermelho', primary: '#ef4444', secondary: '#f97316' },
  { name: 'Amarelo', primary: '#eab308', secondary: '#84cc16' },
  { name: 'Ciano', primary: '#06b6d4', secondary: '#8b5cf6' },
];

const SIDEBAR_COLORS = [
  { name: 'Escuro (Padrão)', value: '#1e293b' },
  { name: 'Preto', value: '#0f172a' },
  { name: 'Cinza', value: '#374151' },
  { name: 'Azul Escuro', value: '#1e3a5f' },
  { name: 'Verde Escuro', value: '#14532d' },
  { name: 'Roxo Escuro', value: '#3b0764' },
];

export default function CustomizationPage() {
  const { t } = useTranslation();
  const { currentInstance, loadInstances } = useInstance();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const logoInputRef = useRef(null);
  const faviconInputRef = useRef(null);
  const [settings, setSettings] = useState({
    logo_url: '',
    favicon_url: '',
    business_name: '',
    theme: {
      primary_color: '#f97316',
      secondary_color: '#3b82f6',
      sidebar_color: '#1e293b',
      header_color: '#0f172a',
      background_mode: 'dark'
    }
  });
  
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    loadSettings();
  }, [currentInstance]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/instances/me');
      if (response.data?.settings) {
        setSettings({
          logo_url: response.data.settings.logo_url || '',
          favicon_url: response.data.settings.favicon_url || '',
          business_name: response.data.settings.business_name || response.data.name || '',
          theme: {
            primary_color: response.data.settings.theme?.primary_color || '#f97316',
            secondary_color: response.data.settings.theme?.secondary_color || '#3b82f6',
            sidebar_color: response.data.settings.theme?.sidebar_color || '#1e293b',
            header_color: response.data.settings.theme?.header_color || '#0f172a',
            background_mode: response.data.settings.theme?.background_mode || 'dark'
          }
        });
      }
    } catch (err) {
      console.log('Could not load customization settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/instances/me/settings', { settings });
      toast.success('Personalização salva com sucesso!');
      loadInstances?.();
    } catch (err) {
      toast.error('Erro ao salvar personalização');
    } finally {
      setSaving(false);
    }
  };

  const handleThemeChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      theme: { ...prev.theme, [key]: value }
    }));
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const applyPalette = (palette) => {
    setSettings(prev => ({
      ...prev,
      theme: {
        ...prev.theme,
        primary_color: palette.primary,
        secondary_color: palette.secondary
      }
    }));
  };

  const resetToDefault = () => {
    setSettings({
      logo_url: '',
      favicon_url: '',
      business_name: '',
      theme: {
        primary_color: '#f97316',
        secondary_color: '#3b82f6',
        sidebar_color: '#1e293b',
        header_color: '#0f172a',
        background_mode: 'dark'
      }
    });
  };

  const handleUploadLogo = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      setUploadingLogo(true);
      const res = await api.post('/instances/me/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSettings(prev => ({ ...prev, logo_url: res.data.logo_url }));
      toast.success('Logo atualizado com sucesso!');
      loadInstances?.();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro ao enviar logo';
      toast.error(msg);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleUploadFavicon = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      setUploadingFavicon(true);
      const res = await api.post('/instances/me/favicon', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSettings(prev => ({ ...prev, favicon_url: res.data.favicon_url }));
      toast.success('Favicon atualizado com sucesso!');
      loadInstances?.();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro ao enviar favicon';
      toast.error(msg);
    } finally {
      setUploadingFavicon(false);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await api.delete('/instances/me/logo');
      setSettings(prev => ({ ...prev, logo_url: '' }));
      toast.success('Logo removido. Usando padrão SisPDV.');
      loadInstances?.();
    } catch (err) {
      toast.error('Erro ao remover logo');
    }
  };

  const handleRemoveFavicon = async () => {
    try {
      await api.delete('/instances/me/favicon');
      setSettings(prev => ({ ...prev, favicon_url: '' }));
      toast.success('Favicon removido. Usando padrão SisPDV.');
      loadInstances?.();
    } catch (err) {
      toast.error('Erro ao remover favicon');
    }
  };

  const handleDrop = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      if (type === 'logo') handleUploadLogo(file);
      else handleUploadFavicon(file);
    } else {
      toast.error('Por favor, envie um arquivo de imagem.');
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">Carregando configurações...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="customization-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Palette className="h-6 w-6" />
            Personalização
          </h1>
          <p className="text-muted-foreground">
            Customize a aparência do seu restaurante
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={resetToDefault}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Restaurar Padrão
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-customization-btn">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </div>

      <HelpAlert variant="tip" title="Dica" className="mb-6">
        As alterações de cores serão aplicadas após salvar e recarregar a página.
        Use a pré-visualização para ver como ficará antes de salvar.
      </HelpAlert>

      <div className="grid gap-6">
        {/* Branding */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5" />
              Marca
            </CardTitle>
            <CardDescription>
              Logo, favicon e nome do seu negócio
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                Nome de Exibição
                <HelpTooltip>
                  Nome que aparecerá nos cupons e relatórios
                </HelpTooltip>
              </Label>
              <Input
                value={settings.business_name}
                onChange={(e) => handleChange('business_name', e.target.value)}
                placeholder="Nome do restaurante"
                data-testid="business-name-input"
              />
            </div>

            <Separator />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Logo Upload */}
              <div className="space-y-3">
                <Label className="flex items-center gap-1 text-base font-semibold">
                  Logo
                  <HelpTooltip>
                    Imagem principal do seu negócio. Aparece na sidebar e no cabeçalho. PNG ou SVG recomendado.
                  </HelpTooltip>
                </Label>
                <div
                  className="relative flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-6 hover:border-primary/50 transition-colors cursor-pointer bg-muted/30"
                  onClick={() => !uploadingLogo && logoInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => handleDrop(e, 'logo')}
                  data-testid="logo-upload-area"
                >
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleUploadLogo(e.target.files?.[0])}
                    data-testid="logo-file-input"
                  />
                  
                  {uploadingLogo ? (
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  ) : (
                    <>
                      <div className="w-20 h-20 rounded-xl overflow-hidden bg-background border flex items-center justify-center mb-3">
                        <img
                          src={settings.logo_url || DEFAULT_LOGOS.iconDark}
                          alt="Logo"
                          className="w-full h-full object-contain"
                          data-testid="logo-preview-img"
                        />
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium">
                          <Upload className="h-4 w-4 inline mr-1" />
                          {settings.logo_url ? 'Trocar logo' : 'Enviar logo'}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Clique ou arraste uma imagem
                        </p>
                        <p className="text-xs text-muted-foreground">
                          PNG, JPG, SVG (máx. 5MB)
                        </p>
                      </div>
                    </>
                  )}
                </div>
                
                {settings.logo_url && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive w-full"
                    onClick={handleRemoveLogo}
                    data-testid="remove-logo-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remover logo (usar padrão)
                  </Button>
                )}
              </div>

              {/* Favicon Upload */}
              <div className="space-y-3">
                <Label className="flex items-center gap-1 text-base font-semibold">
                  Favicon
                  <HelpTooltip>
                    Ícone que aparece na aba do navegador. Use imagem quadrada.
                  </HelpTooltip>
                </Label>
                <div
                  className="relative flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-6 hover:border-primary/50 transition-colors cursor-pointer bg-muted/30"
                  onClick={() => !uploadingFavicon && faviconInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => handleDrop(e, 'favicon')}
                  data-testid="favicon-upload-area"
                >
                  <input
                    ref={faviconInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleUploadFavicon(e.target.files?.[0])}
                    data-testid="favicon-file-input"
                  />
                  
                  {uploadingFavicon ? (
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  ) : (
                    <>
                      <div className="w-16 h-16 rounded-lg overflow-hidden bg-background border flex items-center justify-center mb-3">
                        <img
                          src={settings.favicon_url || DEFAULT_LOGOS.favicon}
                          alt="Favicon"
                          className="w-full h-full object-contain"
                          data-testid="favicon-preview-img"
                        />
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium">
                          <Upload className="h-4 w-4 inline mr-1" />
                          {settings.favicon_url ? 'Trocar favicon' : 'Enviar favicon'}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Clique ou arraste uma imagem
                        </p>
                        <p className="text-xs text-muted-foreground">
                          PNG quadrado (máx. 5MB)
                        </p>
                      </div>
                    </>
                  )}
                </div>
                
                {settings.favicon_url && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive w-full"
                    onClick={handleRemoveFavicon}
                    data-testid="remove-favicon-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remover favicon (usar padrão)
                  </Button>
                )}
              </div>
            </div>

            {!settings.logo_url && !settings.favicon_url && (
              <p className="text-xs text-muted-foreground text-center">
                Nenhum logo customizado. Usando o padrão SisPDV.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Color Palette */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              Paleta de Cores
            </CardTitle>
            <CardDescription>
              Escolha uma paleta pronta ou personalize as cores
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Quick Palettes */}
            <div className="space-y-2">
              <Label>Paletas Rápidas</Label>
              <div className="flex flex-wrap gap-2">
                {COLOR_PALETTES.map((palette) => (
                  <button
                    key={palette.name}
                    onClick={() => applyPalette(palette)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border hover:bg-muted transition-colors"
                    title={palette.name}
                  >
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: palette.primary }}
                    />
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: palette.secondary }}
                    />
                    <span className="text-xs">{palette.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <Separator />

            {/* Custom Colors */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cor Principal</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={settings.theme.primary_color}
                    onChange={(e) => handleThemeChange('primary_color', e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={settings.theme.primary_color}
                    onChange={(e) => handleThemeChange('primary_color', e.target.value)}
                    className="flex-1 font-mono"
                    data-testid="primary-color-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Cor Secundária</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={settings.theme.secondary_color}
                    onChange={(e) => handleThemeChange('secondary_color', e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={settings.theme.secondary_color}
                    onChange={(e) => handleThemeChange('secondary_color', e.target.value)}
                    className="flex-1 font-mono"
                  />
                </div>
              </div>
            </div>

            {/* Preview */}
            <div className="p-4 rounded-lg border bg-muted/30">
              <Label className="mb-3 block">Pré-visualização</Label>
              <div className="flex items-center gap-4">
                <Button style={{ backgroundColor: settings.theme.primary_color }}>
                  Botão Principal
                </Button>
                <Button variant="outline" style={{ 
                  borderColor: settings.theme.secondary_color,
                  color: settings.theme.secondary_color 
                }}>
                  Botão Secundário
                </Button>
                <div 
                  className="w-8 h-8 rounded-full"
                  style={{ backgroundColor: settings.theme.primary_color }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Theme Mode */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {settings.theme.background_mode === 'dark' ? (
                <Moon className="h-5 w-5" />
              ) : (
                <Sun className="h-5 w-5" />
              )}
              Tema
            </CardTitle>
            <CardDescription>
              Configure as cores do layout
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Modo de Fundo</Label>
                <Select
                  value={settings.theme.background_mode}
                  onValueChange={(v) => handleThemeChange('background_mode', v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dark">
                      <span className="flex items-center gap-2">
                        <Moon className="h-4 w-4" /> Escuro
                      </span>
                    </SelectItem>
                    <SelectItem value="light">
                      <span className="flex items-center gap-2">
                        <Sun className="h-4 w-4" /> Claro
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Cor da Sidebar</Label>
                <Select
                  value={settings.theme.sidebar_color}
                  onValueChange={(v) => handleThemeChange('sidebar_color', v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SIDEBAR_COLORS.map((color) => (
                      <SelectItem key={color.value} value={color.value}>
                        <span className="flex items-center gap-2">
                          <div 
                            className="w-4 h-4 rounded"
                            style={{ backgroundColor: color.value }}
                          />
                          {color.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Layout Preview */}
            <div className="p-4 rounded-lg border">
              <Label className="mb-3 block flex items-center gap-2">
                <Eye className="h-4 w-4" />
                Pré-visualização do Layout
              </Label>
              <div className="flex rounded-lg overflow-hidden border h-32">
                {/* Sidebar Preview */}
                <div 
                  className="w-16 flex flex-col items-center py-2 gap-2"
                  style={{ backgroundColor: settings.theme.sidebar_color }}
                >
                  <div className="w-8 h-8 rounded-lg" style={{ backgroundColor: settings.theme.primary_color }} />
                  <div className="w-6 h-6 rounded bg-white/20" />
                  <div className="w-6 h-6 rounded bg-white/20" />
                  <div className="w-6 h-6 rounded bg-white/20" />
                </div>
                {/* Content Preview */}
                <div 
                  className="flex-1 flex flex-col"
                  style={{ backgroundColor: settings.theme.background_mode === 'dark' ? '#0f172a' : '#f8fafc' }}
                >
                  {/* Header Preview */}
                  <div 
                    className="h-10 flex items-center px-3 gap-2"
                    style={{ backgroundColor: settings.theme.header_color }}
                  >
                    <div className="w-16 h-4 rounded bg-white/20" />
                    <div className="flex-1" />
                    <div className="w-6 h-6 rounded-full bg-white/20" />
                  </div>
                  {/* Content */}
                  <div className="flex-1 p-3">
                    <div 
                      className="w-24 h-3 rounded mb-2"
                      style={{ 
                        backgroundColor: settings.theme.background_mode === 'dark' ? '#334155' : '#e2e8f0' 
                      }}
                    />
                    <div 
                      className="w-32 h-3 rounded"
                      style={{ 
                        backgroundColor: settings.theme.background_mode === 'dark' ? '#334155' : '#e2e8f0' 
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
